/**
 * Auth HTTP service.
 *
 * Wraps the two backend endpoints for authentication:
 *   POST /login              → exchange credentials for tokens
 *   POST /refresh/:token     → silently extend an expiring session
 */

import axios from 'axios'
import { getBackendErrorMessage } from '@/lib/apiError'
import { config } from '@/lib/config'
import { resolveClientFeature } from '@/lib/feature/resolveClientFeature'
import { FEATURE_QUERY_PARAM } from '@/lib/feature/feature.constants'
import type { AuthUser, LoginApiResponse, PasswordChangeRequired, RefreshApiResponse } from '../types'

// ---------------------------------------------------------------------------
// Dedicated HTTP client (no bearer token — auth endpoints are unauthenticated)
// ---------------------------------------------------------------------------

const authAxios = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 120_000,
})

// Forward ?feature=<env> al backend para el routeo K8s.
authAxios.interceptors.request.use((cfg) => {
  const feature = resolveClientFeature()
  if (feature) {
    cfg.params = { ...(cfg.params ?? {}), [FEATURE_QUERY_PARAM]: feature }
  }
  return cfg
})

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * POST /login
 * Sends credentials and returns the authenticated user with tokens.
 * Throws a descriptive Error on any failure.
 */
export async function loginWithCredentials(
  email: string,
  password: string,
  locale = 'es',
  messages?: { notFound?: string; loginError?: string },
): Promise<AuthUser | PasswordChangeRequired> {
  try {
    const { data } = await authAxios.post<LoginApiResponse>(
      '/login',
      { Email: email, Password: password },
      { headers: { 'Accept-Language': locale } },
    )

    if (!data?.Data) throw new Error(messages?.notFound ?? 'Usuario no encontrado')

    // El backend no emite bearerToken todavía en este caso — hay que setear
    // una contraseña nueva antes de poder loguear. No es un login exitoso.
    if (data.Data.requirePasswordChange) {
      return { requirePasswordChange: true, refreshToken: data.Data.refreshToken }
    }

    if (!data.Data.bearerToken) throw new Error(messages?.notFound ?? 'Usuario no encontrado')

    return {
      id:           data.Data.id ?? '',
      bearerToken:  data.Data.bearerToken,
      expiration:   data.Data.expiration,
      refreshToken: data.Data.refreshToken,
    }
  } catch (error) {
    throw new Error(getBackendErrorMessage(error, messages?.loginError ?? 'Error al iniciar sesión'))
  }
}

/**
 * POST /login/refresh/:refreshToken
 * Obtains a new bearer token before the current one expires.
 * Throws on failure.
 */
export async function refreshAccessToken(
  bearerToken: string,
  refreshToken: string,
  locale = 'es',
  messages?: { refreshInvalid?: string; loginError?: string },
): Promise<Pick<AuthUser, 'bearerToken' | 'expiration'>> {
  try {
    const { data } = await authAxios.post<RefreshApiResponse>(
      `/login/refresh/${refreshToken}`,
      {},
      { headers: { Authorization: `Bearer ${bearerToken}`, 'Accept-Language': locale } },
    )

    if (!data?.Data?.bearerToken) throw new Error(messages?.refreshInvalid ?? 'Respuesta de refresh inválida')

    return { bearerToken: data.Data.bearerToken, expiration: data.Data.expiration }
  } catch (error) {
    throw new Error(getBackendErrorMessage(error, messages?.loginError ?? 'Error al iniciar sesión'))
  }
}
